import { Icon } from '@iconify/react'
import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { RaspadinhaPublicRegulationDocument } from '@/types/raspadinha-public-regulation-document'
import styles from './RaspadinhaPublicDocumentsModal.module.css'

type RaspadinhaPublicDocumentsModalProps = {
  documents: RaspadinhaPublicRegulationDocument[]
  highlightedDocumentId: string | null
  onClose: () => void
}

export function RaspadinhaPublicDocumentsModal({
  documents,
  highlightedDocumentId,
  onClose,
}: RaspadinhaPublicDocumentsModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    closeButtonRef.current?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [handleKeyDown])

  useEffect(() => {
    if (!highlightedDocumentId) {
      return
    }

    const frameId = window.requestAnimationFrame(() => {
      document
        .getElementById(`raspadinha-document-${highlightedDocumentId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [highlightedDocumentId])

  if (documents.length === 0) {
    return null
  }

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="raspadinha-documents-modal-title"
      >
        <header className={styles.header}>
          <h2 id="raspadinha-documents-modal-title" className={styles.title}>
            Documentos da campanha
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Fechar documentos"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </header>

        <div className={styles.body}>
          <ul className={styles.documentList}>
            {documents.map((document) => {
              const isHighlighted = highlightedDocumentId === document.id

              return (
                <li
                  key={document.id}
                  id={`raspadinha-document-${document.id}`}
                  className={`${styles.documentCard} ${
                    isHighlighted ? styles.documentCardHighlighted : ''
                  }`}
                >
                  <div className={styles.thumbnail} aria-hidden>
                    <Icon className={styles.thumbnailIcon} icon="mdi:file-pdf-box" />
                    <span className={styles.thumbnailLabel}>PDF</span>
                  </div>

                  <div className={styles.documentInfo}>
                    <h3 className={styles.documentTitle}>{document.title}</h3>
                    <p className={styles.documentFileName}>{document.pdf_name}</p>
                    <a
                      className={styles.viewButton}
                      href={document.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon icon="mdi:eye-outline" aria-hidden />
                      Visualizar
                    </a>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>,
    document.body
  )
}
